import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BEAMER_CHANNEL_ID } from 'gcs-common/constants/channelConstants';
import logoutEffect from 'gcs-common/slices/auth/authThunks/logout-effect';
import { channelRemoved, channelsAdded } from 'gcs-common/slices/channels/channelsSlice';
import { allChannelsFetched } from 'gcs-common/slices/channels/channelsThunks/allChannelsFetched';
import { currentUserUpdated } from 'gcs-common/slices/currentUser/currentUserSlice';
import { favoritesUpdated } from 'gcs-common/slices/favorites/favoritesSlice';
import { getSelectedHouseId } from 'gcs-common/slices/houses/housesSelectors';
import { joinedHousesUpdated } from 'gcs-common/slices/houses/housesSlice';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Parameters, RequestBody, Response } from '../../../../../types/typeHelper';
import { ERROR_DIALOG } from '../../../../constants/errorDialogConstants';
import { useHandleQueryError } from '../../../../helper/errorMessages';
import { useApiClient } from '../../client';
import { useGetBeamerChatEnabled, useGetCurrentUserIsAgent } from './current-user-api-selectors';

export enum CurrentUserQueryKeys {
  GET_CURRENT_USER_IS_LOGGED_IN = 'GET_CURRENT_USER_IS_LOGGED_IN',
  GET_CURRENT_USER = 'GET_CURRENT_USER',
  GET_CURRENT_USER_ERP_CUSTOMERS = 'GET_CURRENT_USER_ERP_CUSTOMERS',
  GET_CURRENT_USER_FAVORITES = 'GET_CURRENT_USER_FAVORITES',
  GET_CURRENT_USER_CHANNELS = 'GET_CURRENT_USER_CHANNELS',
  GET_CURRENT_USER_HOUSES = 'GET_CURRENT_USER_HOUSES',
  GET_BEAMER_CHANNEL = 'GET_BEAMER_CHANNEL',
}

export type CurrentUserResponseData = Response<'/chat/current-user', 'get'>;
export type FetchCurrentUserErpCustomersResponseData = Response<'/chat/current-user/erp-customers', 'get'>;
export type FetchCurrentUserHousesResponseData = Response<'/chat/current-user/houses', 'get'>;

export function useGetCurrentUserIsLoggedIn() {
  const client = useApiClient();
  const handleQueryError = useHandleQueryError();
  return useQuery({
    queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER_IS_LOGGED_IN],
    queryFn: async () => {
      const { data, error } = await client.GET('/chat/current-user/session', { credentials: 'include' });
      handleQueryError({
        errorDialog: ERROR_DIALOG.NONE,
        // @ts-expect-error mismatch between openapi-fetch error and backend error types
        error,
      });
      return data;
    },
    retry: false,
    retryOnMount: false,
    staleTime: 1000 * 60 * 30,
  });
}

export function useGetCurrentUser() {
  const client = useApiClient();
  const dispatch = useDispatch();
  return useQuery({
    queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER],
    queryFn: async () => {
      const { data } = await client.GET('/chat/current-user');
      dispatch(currentUserUpdated({ user: data }));
      return data;
    },
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 30,
  });
}

export function useGetCurrentUserChannels({
  query,
}: { query: Parameters<'/chat/current-user/channels', 'get'>['query'] }) {
  const client = useApiClient();
  const dispatch = useDispatch();
  return useQuery({
    queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER_CHANNELS, query],
    queryFn: async () => {
      const { data } = await client.GET('/chat/current-user/channels', {
        params: {
          query,
        },
      });
      // @ts-expect-error -> remove soon
      dispatch(allChannelsFetched({ ...data, replaceChannelTypes: query?.channel_types ?? [] }));
      return data;
    },
  });
}

export function useGetCurrentUserHouses() {
  const client = useApiClient();
  const dispatch = useDispatch();
  return useQuery({
    queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER_HOUSES],
    queryFn: async () => {
      const { data } = await client.GET('/chat/current-user/houses');
      dispatch(joinedHousesUpdated(data));
      return data;
    },
  });
}

export function useUpdateCurrentUserMutation() {
  const queryClient = useQueryClient();
  const client = useApiClient();
  return useMutation({
    mutationFn: async ({ body }: { body: RequestBody<'/chat/current-user', 'patch'> }) => {
      await client.PATCH('/chat/current-user', { body });
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER] });
    },
  });
}

export function useDeleteCurrentUserMutation() {
  const queryClient = useQueryClient();
  const client = useApiClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async () => {
      await client.DELETE('/chat/current-user');
    },
    onSuccess: async () => {
      await queryClient.resetQueries({ queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER] });
      queryClient.clear();
      dispatch(logoutEffect());
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER] });
    },
  });
}

export function useGetCurrentUserErpCustomers({
  query,
}: { query: Parameters<'/chat/current-user/erp-customers', 'get'>['query'] }) {
  const client = useApiClient();
  return useQuery({
    queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER_ERP_CUSTOMERS, query],
    queryFn: async () => {
      const { data } = await client.GET('/chat/current-user/erp-customers', {
        params: {
          query,
        },
      });
      return data;
    },
  });
}

export function useGetCurrentUserFavorites() {
  const client = useApiClient();
  const dispatch = useDispatch();
  return useQuery({
    queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER_FAVORITES],
    queryFn: async () => {
      const { data } = await client.GET('/chat/favorites');
      dispatch(favoritesUpdated({ favorites: data?.favorites }));
      return data;
    },
  });
}

export function useGetBeamerChannel() {
  const queryClient = useQueryClient();
  const client = useApiClient();
  const dispatch = useDispatch();
  const isAgent = useGetCurrentUserIsAgent();
  const selectedHouseId = useSelector(getSelectedHouseId);
  const isBeamerChatEnabled = useGetBeamerChatEnabled();

  // Added to force reload on house change
  // Necessary to use the Fn logic of removing/adding the beamer channel based on whether the house has the FF enabled or not
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [CurrentUserQueryKeys.GET_BEAMER_CHANNEL, isAgent ? undefined : selectedHouseId],
      exact: false,
    });
  }, [isAgent, selectedHouseId, isBeamerChatEnabled]);

  return useQuery({
    queryKey: [
      CurrentUserQueryKeys.GET_BEAMER_CHANNEL,
      isAgent ? undefined : selectedHouseId,
      isBeamerChatEnabled,
      isAgent,
    ],
    queryFn: async () => {
      // the if condition is used instead of using the enabled property of useQuery
      // Cannot use the enabled property since the initial loading of the app depends on this query to finish loading
      // Using enabled will not cause the query to be marked as fetched
      if (!isBeamerChatEnabled || (!isAgent && !selectedHouseId)) {
        // Force remove to handle persisting on mobile and hide on unwanted houses
        dispatch(channelRemoved({ channelId: BEAMER_CHANNEL_ID }));
        return null;
      }
      const { data } = await client.GET('/chat/current-user/beamer-channel', {
        params: {
          query: {
            house_id: isAgent ? undefined : selectedHouseId,
          },
        },
      });
      if (data && isBeamerChatEnabled) {
        dispatch(channelsAdded({ channels: [data.channel] }));
      }
      return data;
    },
    refetchOnWindowFocus: false,
  });
}

export function useDeleteCurrentUserFavoriteMutation() {
  const queryClient = useQueryClient();
  const client = useApiClient();
  return useMutation({
    mutationFn: async ({ favoriteId }: { favoriteId: string }) => {
      await client.DELETE('/chat/favorites/{favorite_id}', {
        params: {
          path: {
            // @ts-expect-error -> remove soon
            favorite_id: favoriteId,
          },
        },
      });
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER_FAVORITES] });
    },
  });
}

export function useAddCurrentUserFavoriteMutation() {
  const queryClient = useQueryClient();
  const client = useApiClient();
  return useMutation({
    mutationFn: async ({ body }: { body: RequestBody<'/chat/favorites', 'post'> }) => {
      await client.POST('/chat/favorites', { body });
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER_FAVORITES] });
    },
  });
}

export function useCurrentUserToggleArchivedChannelMutation() {
  const queryClient = useQueryClient();
  const client = useApiClient();
  return useMutation({
    mutationFn: async ({ channelId }: { channelId: string }) => {
      await client.PATCH('/chat/current-user/archive-channel/{channel_id}', {
        params: {
          path: {
            channel_id: channelId,
          },
        },
      });
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: [CurrentUserQueryKeys.GET_CURRENT_USER] });
    },
  });
}
